import axios from "axios";
import { auth } from "../firebase/config";

/* ===============================
   BASE URL CONFIG
================================ */

const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // ✅ 60 seconds — allows Render free tier to wake up from sleep
  headers: {
    "Content-Type": "application/json",
  },
});

/* ===============================
   REQUEST INTERCEPTOR
   - Attaches Firebase ID token
   - Forces token refresh if needed
================================ */

api.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;

      if (user) {
        // Force refresh token if expired
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      console.error("❌ Error attaching auth token:", error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

/* ===============================
   RESPONSE INTERCEPTOR
   - Handles 401 globally
   - Handles timeouts with user message
   - Handles network errors
   - Prevents infinite redirect loops
================================ */

api.interceptors.response.use(
  (response) => response,
  async (error) => {

    // ⏳ Timeout — Render free tier is waking up from sleep
    if (error.code === "ECONNABORTED") {
      console.error("⏳ Request timeout. Backend may be waking up from sleep.");
      error.userMessage =
        "Server is starting up. Please wait 30 seconds and try again ⏳";
      return Promise.reject(error);
    }

    // 🌐 No response at all — complete network failure
    if (!error.response) {
      console.error("🌐 Network error:", error.message);
      error.userMessage =
        "Unable to reach the server. Please check your internet connection.";
      return Promise.reject(error);
    }

    // ✅ Response received — handle by status code
    const status = error.response.status;

    // 🔐 Unauthorized — session expired
    if (status === 401) {
      console.warn("⚠️ Session expired or unauthorized. Logging out...");

      try {
        await auth.signOut();
      } catch (e) {
        console.error("❌ Error signing out:", e);
      }

      localStorage.clear();

      // Prevent multiple redirects
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }

      error.userMessage = "Your session has expired. Please log in again.";
      return Promise.reject(error);
    }

    // ⛔ Forbidden
    if (status === 403) {
      console.warn("⛔ Access denied:", error.config?.url);
      error.userMessage =
        "You do not have permission to perform this action.";
      return Promise.reject(error);
    }

    // 🔍 Not found
    if (status === 404) {
      console.warn("🔍 Resource not found:", error.config?.url);
      error.userMessage = "The requested resource was not found.";
      return Promise.reject(error);
    }

    // 🚨 Server error
    if (status >= 500) {
      console.error("🚨 Server error:", {
        status,
        url: error.config?.url,
        message: error.response.data?.message,
      });
      error.userMessage =
        "Something went wrong on our end. Please try again later.";
      return Promise.reject(error);
    }

    // 📋 All other 4xx errors — log for debugging
    if (status >= 400) {
      console.error("API Error:", {
        status,
        url: error.config?.url,
        message: error.response.data?.message,
      });
      error.userMessage =
        error.response.data?.message || "An error occurred. Please try again.";
    }

    return Promise.reject(error);
  }
);

export default api;